import React, {useEffect, useState} from 'react';
import {Button, DatePicker, Input, InputNumber, Select, Table, Tag} from 'antd';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import type {ColumnsType} from 'antd/es/table';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import {useBcpCheckResultListQuery} from './hooks/useBcpCheckResultListQuery';
import {BCP_CHECK_RESULT_CODES} from './graphql/queries/bcpCheckResultCodeQueries';
import {CheckResultCodeResponse, CheckResultType} from './graphql/types/BcpCheckResultTypes';
import {formatTime} from './utils/timeFormat';
import {
    serializeCheckResultFilters,
    useBcpCheckResultListStore,
} from './store/bcpCheckResultListStore';

const statusOptions = [
    {label: '全部', value: undefined},
    {label: '待处理', value: 0},
    {label: '已确认', value: 1},
    {label: '无需确认', value: 2},
];

const BcpCheckResultListPage: React.FC = () => {
    const navigate = useNavigate();
    const {
        tempFilters,
        filters,
        currentPage,
        pageSize,
        needRefetch,
        setTempFilters,
        applyFilters,
        resetFilters,
        setPage,
        clearNeedRefetch,
    } = useBcpCheckResultListStore();

    const {data, loading, refetch, totalElements} = useBcpCheckResultListQuery(
        filters,
        currentPage,
        pageSize,
        true,
    );

    // 详情页「确认」成功后返回，以原条件强制刷新一次
    useEffect(() => {
        if (needRefetch) {
            clearNeedRefetch();
            refetch();
        }
    }, [needRefetch]);

    const {data: resultCodesData} = useQuery<CheckResultCodeResponse>(BCP_CHECK_RESULT_CODES);

    const [resultCodeOptions, setResultCodeOptions] = useState<{label: string; value: number}[]>([]);

    useEffect(() => {
        const codes = resultCodesData?.bcpCheckResultCodes;
        if (codes) {
            setResultCodeOptions([
                {label: '全部', value: undefined as any},
                ...codes.map((c) => ({label: `${c.description}(${c.code})`, value: c.code})),
            ]);
        }
    }, [resultCodesData]);

    const handleSearch = () => {
        // 条件未变时 cache-first 不会发请求，显式查询需强制刷新
        if (serializeCheckResultFilters(tempFilters) === serializeCheckResultFilters(filters) && currentPage === 1) {
            refetch();
        } else {
            applyFilters();
        }
    };

    const handleReset = () => {
        resetFilters();
    };

    const handlePageChange = (page: number, size: number) => {
        setPage(page, size);
    };

    const columns: ColumnsType<CheckResultType> = [
        {title: 'ID', dataIndex: 'id', key: 'id', width: 80},
        {title: '任务ID', dataIndex: 'jobId', key: 'jobId', width: 80},
        {title: '分组Key', dataIndex: 'groupKey', key: 'groupKey', width: 150},
        {
            title: '核对结果',
            dataIndex: 'resultCode',
            key: 'resultCode',
            width: 100,
            render: (code: number, record: CheckResultType) => (
                <span>{record.resultCodeDesc || code}</span>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (s: number) => {
                if (s === 0) return <Tag color="orange">待处理</Tag>;
                if (s === 1) return <Tag color="green">已确认</Tag>;
                if (s === 2) return <Tag>无需确认</Tag>;
                return <Tag>{s}</Tag>;
            },
        },
        {title: '告警次数', dataIndex: 'alarmCount', key: 'alarmCount', width: 80},
        {title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180, render: (t: string) => formatTime(t)},
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: unknown, record: CheckResultType) => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => navigate(`/ops-tool/settlement/bcp/check-results/${record.id}`)}
                >
                    详情
                </Button>
            ),
        },
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-3">
            <h2 className="text-base font-semibold mb-4">差错处理</h2>
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <div className="flex items-center">
                    <span className="w-[5em] flex-shrink-0 text-right pr-2">任务ID</span>
                    <InputNumber
                        className="w-full"
                        value={tempFilters.jobId}
                        onChange={(v) => setTempFilters({...tempFilters, jobId: v ?? undefined})}
                        placeholder="请输入任务ID"
                        min={0}
                    />
                </div>
                <div className="flex items-center">
                    <span className="w-[5em] flex-shrink-0 text-right pr-2">分组Key</span>
                    <Input
                        className="w-full"
                        value={tempFilters.groupKey || ''}
                        onChange={(e) => setTempFilters({...tempFilters, groupKey: e.target.value || undefined})}
                        placeholder="请输入分组Key"
                        allowClear
                    />
                </div>
                <div className="flex items-center">
                    <span className="w-[4em] flex-shrink-0 text-right pr-2">状态</span>
                    <Select
                        className="w-full"
                        value={tempFilters.status}
                        onChange={(v) => setTempFilters({...tempFilters, status: v})}
                        options={statusOptions}
                        placeholder="全部"
                        allowClear
                    />
                </div>
                <div className="flex items-center">
                    <span className="w-[5em] flex-shrink-0 text-right pr-2">核对结果</span>
                    <Select
                        className="w-full"
                        value={tempFilters.resultCode}
                        onChange={(v) => setTempFilters({...tempFilters, resultCode: v ?? undefined})}
                        options={resultCodeOptions}
                        placeholder="全部"
                        allowClear
                    />
                </div>
                <div className="flex items-center">
                    <span className="w-[5em] flex-shrink-0 text-right pr-2">创建时间</span>
                    <DatePicker.RangePicker
                        className="w-full"
                        showTime
                        value={tempFilters.createTimeRange ?? null}
                        onChange={(range) =>
                            setTempFilters({
                                ...tempFilters,
                                createTimeRange: range ? [range[0], range[1]] : null,
                            })
                        }
                    />
                </div>
            </div>
            <div className="flex gap-2 mb-4">
                <Button type="primary" onClick={handleSearch}>查询</Button>
                <Button onClick={handleReset}>重置</Button>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{x: 1200}}
            />
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />
        </div>
    );
};

export default BcpCheckResultListPage;
